import { supabase } from './supabase';
import { uploadToR2, deleteFromR2 } from './storage';

// Types
export interface ChatMessage {
    id: string;
    project_id: string;
    user_id: string;
    content: string | null;
    message_type: 'text' | 'image' | 'gif' | 'system';
    attachment_url: string | null;
    reply_to_id: string | null;
    is_deleted: boolean;
    created_at: string;
    updated_at: string;
    // Joined data
    user?: {
        id: string;
        username: string;
        full_name?: string;
        avatar_url?: string;
    };
    reply_to?: ChatMessage;
    reactions?: ChatReaction[];
}

export interface ChatReaction {
    id: string;
    message_id: string;
    user_id: string;
    emoji: string;
    created_at: string;
    user?: {
        id: string;
        username: string;
        avatar_url?: string;
    };
}

export interface ReactionCount {
    emoji: string;
    count: number;
    users: { id: string; username: string }[];
    hasReacted: boolean; // Current user has reacted with this emoji
}

export const ChatService = {
    /**
     * Get messages for a project with pagination
     * @param projectId - The project ID
     * @param limit - Number of messages to fetch (default 50)
     * @param before - Fetch messages before this timestamp (for pagination)
     */
    getMessages: async (
        projectId: string,
        limit: number = 50,
        before?: string
    ): Promise<ChatMessage[]> => {
        // Fetch messages only (no joins - FK relationships may not exist)
        let query = supabase
            .from('chat_messages')
            .select('*')
            .eq('project_id', projectId)
            .eq('is_deleted', false)
            .order('created_at', { ascending: false })
            .limit(limit);

        if (before) {
            query = query.lt('created_at', before);
        }

        const { data: messages, error } = await query;

        if (error) {
            console.error('ChatService: Error fetching messages', error);
            return [];
        }

        if (!messages || messages.length === 0) {
            return [];
        }

        // Get unique user IDs and fetch profiles separately
        const userIds = [...new Set(messages.map(m => m.user_id))];
        const { data: profiles, error: profilesError } = await supabase
            .from('profiles')
            .select('id, username, full_name, avatar_url')
            .in('id', userIds);

        if (profilesError) {
            console.error('ChatService: Error fetching profiles', profilesError);
        }

        // Create user lookup map
        const userMap = new Map<string, any>();
        if (profiles && profiles.length > 0) {
            profiles.forEach(p => userMap.set(p.id, p));
        } else {
            console.warn('ChatService: No profiles found for user IDs:', userIds);
        }

        // Fetch reactions for all messages in one query
        const messageIds = messages.map(m => m.id);
        const { data: allReactions } = await supabase
            .from('chat_reactions')
            .select('id, emoji, user_id, message_id')
            .in('message_id', messageIds);

        // Map reactions to messages
        const reactionsMap = new Map<string, ChatReaction[]>();
        if (allReactions) {
            allReactions.forEach((r: any) => {
                const existing = reactionsMap.get(r.message_id) || [];
                existing.push(r);
                reactionsMap.set(r.message_id, existing);
            });
        }

        // Combine messages with users and reactions
        const messagesWithData = messages.map(msg => ({
            ...msg,
            user: userMap.get(msg.user_id) || null,
            reactions: reactionsMap.get(msg.id) || []
        }));

        // Reverse to get chronological order (oldest first)
        return (messagesWithData as ChatMessage[]).reverse();
    },

    /**
     * Get a single message with its reactions
     */
    getMessage: async (messageId: string): Promise<ChatMessage | null> => {
        // Fetch message only (no FK joins)
        const { data: message, error: msgError } = await supabase
            .from('chat_messages')
            .select('*')
            .eq('id', messageId)
            .single();

        if (msgError) {
            console.error('ChatService: Error fetching message', msgError);
            return null;
        }

        // Fetch user profile separately
        const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('id, username, full_name, avatar_url')
            .eq('id', message.user_id)
            .single();

        if (profileError) {
            console.error('ChatService: Error fetching profile for message', profileError);
        }

        // Fetch reactions separately
        const { data: reactions } = await supabase
            .from('chat_reactions')
            .select('id, emoji, user_id')
            .eq('message_id', messageId);

        return {
            ...message,
            user: profile || null,
            reactions: reactions || []
        } as ChatMessage;
    },

    /**
     * Send a new message
     */
    sendMessage: async (
        projectId: string,
        content: string,
        type: 'text' | 'image' | 'gif' | 'system' = 'text',
        attachmentUrl?: string,
        replyToId?: string
    ): Promise<ChatMessage | null> => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return null;

        // Insert message (no FK joins)
        const { data: message, error } = await supabase
            .from('chat_messages')
            .insert({
                project_id: projectId,
                user_id: user.id,
                content: content || null,
                message_type: type,
                attachment_url: attachmentUrl || null,
                reply_to_id: replyToId || null
            })
            .select('*')
            .single();

        if (error) {
            console.error('ChatService: Error sending message', error);
            return null;
        }

        // Fetch user profile separately
        const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('id, username, full_name, avatar_url')
            .eq('id', user.id)
            .single();

        if (profileError) {
            console.error('ChatService: Error fetching sender profile', profileError);
        }

        return {
            ...message,
            user: profile || { id: user.id, username: user.email?.split('@')[0] || 'Unknown', full_name: null, avatar_url: null },
            reactions: []
        } as ChatMessage;
    },

    /**
     * Soft-delete a message (marks is_deleted = true)
     * Also deletes uploaded images from R2 storage (in background)
     */
    deleteMessage: async (messageId: string): Promise<boolean> => {
        // First fetch the message to check for attachments
        const { data: message, error: fetchError } = await supabase
            .from('chat_messages')
            .select('attachment_url, message_type')
            .eq('id', messageId)
            .single();

        if (fetchError) {
            console.error('ChatService: Error fetching message for deletion', fetchError);
            return false;
        }

        // Soft-delete the message first (fast - responsive UI)
        const { error } = await supabase
            .from('chat_messages')
            .update({ is_deleted: true, updated_at: new Date().toISOString() })
            .eq('id', messageId);

        if (error) {
            console.error('ChatService: Error deleting message', error);
            return false;
        }

        // Delete from R2 in background - ONLY for chat uploads (fire and forget - don't block UI)
        // Don't delete lookbook/generation images that were sent to chat
        if (message?.attachment_url && message.message_type === 'image') {
            // Check if this is a chat upload (in chat/uploads/ path) vs a lookbook image
            const isChatUpload = message.attachment_url.includes('/chat/uploads/');

            if (isChatUpload) {
                deleteFromR2(message.attachment_url)
                    .then(() => console.log('ChatService: Deleted chat upload from R2'))
                    .catch(r2Error => console.warn('ChatService: Failed to delete chat upload from R2', r2Error));
            } else {
                console.log('ChatService: Skipping R2 deletion for lookbook/generation image');
            }
        }

        return true;
    },

    /**
     * Upload an image for chat and return the URL
     * Uploads to R2 at /chat/uploads/{project_id}/
     */
    uploadChatImage: async (file: File, projectId: string): Promise<string | null> => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return null;

        try {
            // Upload to R2 at chat/uploads/{project_id}/
            const publicUrl = await uploadToR2(file, `chat/uploads/${projectId}`, user.id);
            return publicUrl;
        } catch (error) {
            console.error('ChatService: Error uploading image to R2', error);
            return null;
        }
    },

    /**
     * Add a reaction to a message
     */
    addReaction: async (messageId: string, emoji: string): Promise<boolean> => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return false;

        const { error } = await supabase
            .from('chat_reactions')
            .insert({
                message_id: messageId,
                user_id: user.id,
                emoji
            });

        if (error) {
            // Might be a duplicate, which is fine
            if (error.code === '23505') {
                return true; // Already reacted
            }
            console.error('ChatService: Error adding reaction', error);
            return false;
        }

        return true;
    },

    /**
     * Remove a reaction from a message
     */
    removeReaction: async (messageId: string, emoji: string): Promise<boolean> => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return false;

        const { error } = await supabase
            .from('chat_reactions')
            .delete()
            .eq('message_id', messageId)
            .eq('user_id', user.id)
            .eq('emoji', emoji);

        if (error) {
            console.error('ChatService: Error removing reaction', error);
            return false;
        }

        return true;
    },

    /**
     * Toggle a reaction (add if not exists, remove if exists)
     */
    toggleReaction: async (messageId: string, emoji: string): Promise<{ added: boolean }> => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return { added: false };

        // Check if reaction exists
        const { data: existing } = await supabase
            .from('chat_reactions')
            .select('id')
            .eq('message_id', messageId)
            .eq('user_id', user.id)
            .eq('emoji', emoji)
            .single();

        if (existing) {
            await ChatService.removeReaction(messageId, emoji);
            return { added: false };
        } else {
            await ChatService.addReaction(messageId, emoji);
            return { added: true };
        }
    },

    /**
     * Get reactions for a message, grouped by emoji with counts
     */
    getReactionCounts: async (messageId: string): Promise<ReactionCount[]> => {
        const { data: { user } } = await supabase.auth.getUser();
        const currentUserId = user?.id;

        const { data, error } = await supabase
            .from('chat_reactions')
            .select('emoji, user_id')
            .eq('message_id', messageId);

        if (error || !data) return [];

        // Group by emoji
        const groups: Map<string, ReactionCount> = new Map();

        data.forEach((reaction: any) => {
            const existing = groups.get(reaction.emoji);
            if (existing) {
                existing.count++;
                existing.users.push({ id: reaction.user_id, username: '' });
                if (reaction.user_id === currentUserId) {
                    existing.hasReacted = true;
                }
            } else {
                groups.set(reaction.emoji, {
                    emoji: reaction.emoji,
                    count: 1,
                    users: [{ id: reaction.user_id, username: '' }],
                    hasReacted: reaction.user_id === currentUserId
                });
            }
        });

        return Array.from(groups.values());
    },

    /**
     * Subscribe to new messages in a project
     */
    subscribeToMessages: (
        projectId: string,
        onMessage: (message: ChatMessage) => void
    ) => {
        return supabase
            .channel(`project_chat:${projectId}`)
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'chat_messages',
                    filter: `project_id=eq.${projectId}`
                },
                async (payload) => {
                    // Fetch the full message with user data
                    const message = await ChatService.getMessage(payload.new.id);
                    if (message) {
                        onMessage(message);
                    }
                }
            )
            .subscribe();
    },

    /**
     * Subscribe to reaction changes in a project
     */
    subscribeToReactions: (
        projectId: string,
        onReactionChange: (messageId: string, reactions: ChatReaction[]) => void
    ) => {
        // We need to listen to both inserts and deletes on chat_reactions
        // But filter by project_id through the message
        // This is tricky - we'll need to refetch reactions for the message
        return supabase
            .channel(`project_reactions:${projectId}`)
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'chat_reactions'
                },
                async (payload) => {
                    const messageId = payload.new?.message_id || payload.old?.message_id;
                    if (!messageId) return;

                    // Check if this message belongs to our project
                    const { data: message } = await supabase
                        .from('chat_messages')
                        .select('id, project_id')
                        .eq('id', messageId)
                        .single();

                    if (message?.project_id !== projectId) return;

                    // Fetch updated reactions (simplified query)
                    const { data: reactions } = await supabase
                        .from('chat_reactions')
                        .select('id, emoji, user_id, message_id, created_at')
                        .eq('message_id', messageId);

                    if (reactions) {
                        onReactionChange(messageId, reactions as ChatReaction[]);
                    }
                }
            )
            .subscribe();
    },

    /**
     * Send a system message (e.g., "User joined the project")
     */
    sendSystemMessage: async (
        projectId: string,
        content: string
    ): Promise<ChatMessage | null> => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return null;

        return ChatService.sendMessage(projectId, content, 'system');
    }
};

// Tenor GIF API helpers
export const TenorService = {
    /**
     * Search for GIFs using Tenor API
     */
    searchGifs: async (
        query: string,
        limit: number = 20,
        apiKey?: string
    ): Promise<{ id: string; url: string; preview: string; width: number; height: number }[]> => {
        const key = apiKey || import.meta.env.VITE_TENOR_API_KEY;
        if (!key) {
            console.error('TenorService: No API key configured');
            return [];
        }

        try {
            const response = await fetch(
                `https://tenor.googleapis.com/v2/search?q=${encodeURIComponent(query)}&key=${key}&limit=${limit}&media_filter=gif,tinygif`
            );

            if (!response.ok) {
                throw new Error('Tenor API request failed');
            }

            const data = await response.json();

            return data.results.map((result: any) => ({
                id: result.id,
                url: result.media_formats.gif.url,
                preview: result.media_formats.tinygif.url,
                width: result.media_formats.gif.dims[0],
                height: result.media_formats.gif.dims[1]
            }));
        } catch (error) {
            console.error('TenorService: Error searching GIFs', error);
            return [];
        }
    },

    /**
     * Get trending GIFs from Tenor
     */
    getTrendingGifs: async (
        limit: number = 20,
        apiKey?: string
    ): Promise<{ id: string; url: string; preview: string; width: number; height: number }[]> => {
        const key = apiKey || import.meta.env.VITE_TENOR_API_KEY;
        if (!key) {
            console.error('TenorService: No API key configured');
            return [];
        }

        try {
            const response = await fetch(
                `https://tenor.googleapis.com/v2/featured?key=${key}&limit=${limit}&media_filter=gif,tinygif`
            );

            if (!response.ok) {
                throw new Error('Tenor API request failed');
            }

            const data = await response.json();

            return data.results.map((result: any) => ({
                id: result.id,
                url: result.media_formats.gif.url,
                preview: result.media_formats.tinygif.url,
                width: result.media_formats.gif.dims[0],
                height: result.media_formats.gif.dims[1]
            }));
        } catch (error) {
            console.error('TenorService: Error fetching trending GIFs', error);
            return [];
        }
    }
};
